import { call, delay, put, takeEvery } from 'redux-saga/effects'
import nautilusApi from '../../nautilusApi'
import pickBy from 'lodash/pickBy'

import { addNotification, removeNotification } from '../../store/notifications'
import {
  promoReportsRequest,
  promoReportsResponse,
  promoReportsCreateRequest,
  promoReportsCreateResponse,
  usersListRequest,
  usersListResponse,
  changeUserStatus,
  changeUserStatusResponse,
  changeOrgUserStatus,
  changeOrgUserStatusResponse,
  promoReportsUpdateRequest,
  promoReportsUpdateResponse,
  promoReportsDeleteRequest,
  promoReportsDeleteResponse,
  reportFormToggle,
  resendConfirmationRequest,
} from './actions'

function buildQuery(params) {
  const query = {
    ...pickBy(params),
    page: params.page || 1,
    per_page: params.per_page || 100,
  }

  return query
}

const usersList = async (action) => {
  let response
  if (action.payload.orgUid) {
    response = await nautilusApi
      .fetch(`/organizations/${action.payload.orgUid}/memberships`, { query: buildQuery(action.payload.params) })
  } else {
    response = await nautilusApi
      .fetch('/admin/users', { query: buildQuery(action.payload.params) })
  }

  return response
}

const callChangeUserStatus = async (action) => {
  const status = action.payload.status.toLowerCase()
  if (status === 'active') {
    return nautilusApi
      .fetch(`/admin/users/approve/${action.payload.id}`, {
        method: 'PUT',
      })
  } else if (status === 'denied') {
    return nautilusApi
      .fetch(`/admin/users/reject/${action.payload.id}`, {
        method: 'PUT',
      })
  } else if (status === 'disabled') {
    return nautilusApi
      .fetch(`/admin/users/${action.payload.id}/disable`, {
        method: 'PUT',
      })
  }
}

const callChangeOrgUserStatus = async (action) => {
  const status = action.payload.status.toLowerCase()
  if (status === 'active') {
    return nautilusApi
      .fetch(`/organizations/${action.payload.orgUid}/memberships/${action.payload.membership_id}/status_update?status_update=approve`, {
        method: 'PUT',
      })
  } else if (status === 'denied') {
    return nautilusApi
      .fetch(`/organizations/${action.payload.orgUid}/memberships/${action.payload.membership_id}/status_update?status_update=deny`, {
        method: 'PUT',
      })
  } else if (status === 'disabled') {
    return nautilusApi
      .fetch(`/organizations/${action.payload.orgUid}/memberships/${action.payload.membership_id}/status_update?status_update=disable`, {
        method: 'PUT',
      })
  }
}

const resendConfirmation = async (action) => {
  return nautilusApi
    .fetch(`/admin/confirmations/resend/${action.payload.id}`, {
      method: 'POST',
    })
}

const promoReports = {
  list: () => nautilusApi.fetch('/admin/promoted_reports'),

  create: (action) => {
    const formData = new FormData()
    Object.entries(action.payload).forEach(([key, value]) => {
      formData.append(`promoted_report[${key}]`, value)
    })

    return nautilusApi.fetch('/admin/promoted_reports', {
      method: 'POST',
      body: formData,
      headers: { 'content-type': false }
    })
  },

  update: async (action) => {
    const path = '/admin/promoted_reports/' + action?.payload?.id

    const formData = new FormData()
    Object.entries(action.payload).forEach(([key, value]) => {
      formData.append(`promoted_report[${key}]`, value)
    })

    return nautilusApi.fetch(path, {
      method: 'PUT',
      body: formData,
      headers: { 'content-type': false }
    })
  },

  delete: (action) => {
    const path = '/admin/promoted_reports/' + action?.payload
    return nautilusApi.fetch(path, {
      method: 'DELETE',
      as: false,
    })
  }
}

export function * promoReportsSaga() {
  try {
    const response = yield call(promoReports.list)
    yield put(promoReportsResponse(response))
  } catch (error) {
    yield put(promoReportsResponse(error))
  }
}

export function * promoReportsCreationSaga(action) {
  try {
    yield call(promoReports.create, action)
    yield put(promoReportsRequest())
    yield put(reportFormToggle())
    yield call(successNotification, { source: 'Promoted Report', operation: 'created' })
  } catch (error) {
    yield call(errorNotification, { source: 'Promoted Report' })
    yield put(promoReportsCreateResponse(error))
  }
}

export function * usersListSaga(action) {
  try {
    const response = yield call(usersList, action)
    yield put(usersListResponse(response))
  } catch (error) {
    yield put(usersListResponse(error))
  }
}

function * showNotification(message, options) {
  const notification = yield put(addNotification(message, options))
  yield delay(5000)
  yield put(removeNotification(notification.payload.id))
}

function * successNotification({ source, operation }) {
  yield call(showNotification,
    `${source} successfully ${operation}.`,
    {
      source,
      type: 'success',
    }
  )
}

function * errorNotification({ source }) {
  yield call(showNotification,
    'There was a problem with your request. Please try again, or contact support.',
    {
      source,
      type: 'error',
    }
  )
}

export function * changeUserSaga(action) {
  try {
    yield call(callChangeUserStatus, action)
    yield put(usersListRequest({ params: action.payload.params }))
    yield call(successNotification, { source: 'User Change', operation: 'updated' })
  } catch (error) {
    yield call(errorNotification, { source: 'User Change' })
    yield put(changeUserStatusResponse(error))
  }
}

export function * changeOrgUserSaga(action) {
  try {
    yield call(callChangeOrgUserStatus, action)
    const { orgUid, params } = action.payload
    yield put(usersListRequest({ orgUid, params }))
    yield call(successNotification, { source: 'User Change', operation: 'updated' })
  } catch (error) {
    yield call(errorNotification, { source: 'User Change' })
    yield put(changeOrgUserStatusResponse(error))
  }
}

export function * resendConfirmationSaga(action) {
  try {
    yield call(resendConfirmation, action)
    yield put(usersListRequest({ params: action.payload.params }))
  } catch (error) {
    yield put(changeUserStatusResponse(error))
  }
}

export function * promoReportsUpdateSaga(action) {
  try {
    yield call(promoReports.update, action)
    yield put(reportFormToggle(false))
    yield put(promoReportsRequest())
    yield call(successNotification, { source: 'Promoted Report', operation: 'updated' })
  } catch (error) {
    yield call(errorNotification, { source: 'Promoted Report' })
    yield put(promoReportsUpdateResponse(error))
  }
}

export function * promoReportsDeleteSaga(action) {
  try {
    yield call(promoReports.delete, action)
    yield put(promoReportsRequest())
    yield call(successNotification, { source: 'Promoted Report', operation: 'deleted' })
  } catch (error) {
    yield call(errorNotification, { source: 'Promoted Report' })
    yield put(promoReportsDeleteResponse(error))
  }
}

export default function * saga() {
  yield takeEvery(promoReportsRequest, promoReportsSaga)
  yield takeEvery(promoReportsCreateRequest, promoReportsCreationSaga)
  yield takeEvery(usersListRequest, usersListSaga)
  yield takeEvery(changeUserStatus, changeUserSaga)
  yield takeEvery(changeOrgUserStatus, changeOrgUserSaga)
  yield takeEvery(promoReportsUpdateRequest, promoReportsUpdateSaga)
  yield takeEvery(promoReportsDeleteRequest, promoReportsDeleteSaga)
  yield takeEvery(resendConfirmationRequest, resendConfirmationSaga)
}
